'use client';

import { useState, useEffect, useCallback } from 'react';
import { slugify } from '@/lib/slug';

export interface Country {
  id: string;
  name: string;
  iso3: string;
  iso2: string;
  area_km2: number;
  area_rank: number;
  continent: string;
  flag: string;
  center: [number, number];
  bbox: [number, number, number, number];
  path: string;
  slug: string; // 添加 slug 字段
}

export interface CountriesStats {
  totalCountries: number;
  totalArea: number;
  totalPopulation: number;
  continents: {
    [key: string]: number;
  };
}

export function useCountries() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [stats, setStats] = useState<CountriesStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCountries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/data/minis/countries-mini.json');
      if (!response.ok) {
        throw new Error('Failed to load countries data');
      }
      
      const data = await response.json();
      
      // 处理数据，确保所有必要字段都存在
      const processedCountries: Country[] = data.map((country: any) => ({
        id: country.id || country.iso3,
        name: country.name || 'Unknown',
        iso3: country.iso3 || country.id,
        iso2: country.iso2 || '',
        area_km2: country.area_km2 || 0,
        area_rank: country.area_rank || 999,
        continent: country.continent || 'Unknown',
        flag: country.flag || '',
        center: country.center || [0, 0],
        bbox: country.bbox || [0, 0, 0, 0],
        path: country.path || `data/countries/${country.id || country.iso3}.json`,
        slug: slugify(country.name || 'Unknown') // 生成 slug
      }));

      // 按面积排序
      processedCountries.sort((a, b) => b.area_km2 - a.area_km2);
      
      setCountries(processedCountries);

      // 计算统计信息
      const totalArea = processedCountries.reduce((sum, country) => sum + country.area_km2, 0);
      const continents: { [key: string]: number } = {};
      
      processedCountries.forEach(country => {
        continents[country.continent] = (continents[country.continent] || 0) + 1;
      });

      setStats({
        totalCountries: processedCountries.length,
        totalArea: Math.round(totalArea),
        totalPopulation: 0, // 暂时设为0，后续可以从其他数据源获取
        continents
      });

    } catch (err) {
      console.error('Error loading countries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load countries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  // 搜索国家
  const searchCountries = useCallback((query: string) => {
    if (!query.trim()) return countries;
    
    const lowercaseQuery = query.toLowerCase();
    return countries.filter(country => 
      country.name.toLowerCase().includes(lowercaseQuery) ||
      country.iso3.toLowerCase().includes(lowercaseQuery) ||
      country.iso2.toLowerCase().includes(lowercaseQuery)
    );
  }, [countries]);

  // 按大洲筛选
  const filterByContinent = useCallback((continent: string) => {
    if (!continent || continent === 'all') return countries;
    return countries.filter(country => country.continent === continent);
  }, [countries]);

  // 排序
  const sortCountries = useCallback((sortBy: 'area' | 'name', order: 'asc' | 'desc' = 'desc') => {
    const sorted = [...countries];
    
    switch (sortBy) {
      case 'area':
        sorted.sort((a, b) => order === 'desc' ? b.area_km2 - a.area_km2 : a.area_km2 - b.area_km2);
        break;
      case 'name':
        sorted.sort((a, b) => order === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name));
        break;
    }
    
    return sorted;
  }, [countries]);

  return {
    countries,
    stats,
    loading,
    error,
    searchCountries,
    filterByContinent,
    sortCountries,
    refetch: loadCountries
  };
}
